import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { useIntersectionObserver } from '@subscriptions/presentation/hooks';
import { PaymentDetailSheet } from './PaymentDetailSheet';
import { PaymentHistoryMobileListSkeleton } from './PaymentHistoryMobileListSkeleton';
import { PaymentSummaryCard } from './PaymentSummaryCard';

interface PaymentHistoryMobileListProps {
  blockUuid?: string;
  payments: PaymentHistoryViewDto[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  showSubscription?: boolean;
}

/**
 * PaymentHistoryMobileList - Stacked-card view of payment history for narrow viewports.
 *
 * Each payment renders as a clickable {@link PaymentSummaryCard} that opens a detail sheet.
 * Grouped payments (those with `relatedInvoices`) expose their children inside the detail
 * sheet rather than inline, so the list stays a flat stack of top-level summaries.
 *
 * When pagination props are provided, renders a sentinel + skeleton cards + a
 * manual "Cargar más" button mirroring the table behavior.
 */
export const PaymentHistoryMobileList = ({
  blockUuid,
  payments,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  showSubscription = true,
}: PaymentHistoryMobileListProps) => {
  const { t } = useTranslation('paymentHistory');
  const [selectedPayment, setSelectedPayment] = useState<PaymentHistoryViewDto | null>(null);

  const canLoadMore = hasNextPage && !isFetchingNextPage && !!onLoadMore;

  const handleLoadMore = () => {
    if (canLoadMore) {
      onLoadMore();
    }
  };

  const observerRef = useIntersectionObserver({
    onIntersect: handleLoadMore,
    enabled: canLoadMore,
    threshold: 1.0,
  });

  return (
    <div className="flex flex-col gap-3">
      {payments.map((payment) => (
        <PaymentSummaryCard
          key={payment.uuid}
          onClick={setSelectedPayment}
          payment={payment}
          showSubscription={showSubscription}
        />
      ))}

      {hasNextPage && !isFetchingNextPage && (
        <div className="h-1" data-testid="payment-history-mobile-infinite-scroll-trigger" ref={observerRef} />
      )}

      {isFetchingNextPage && <PaymentHistoryMobileListSkeleton />}

      {canLoadMore && (
        <Button
          className="self-center"
          data-testid="payment-history-mobile-load-more-button"
          onClick={handleLoadMore}
          size="sm"
          variant="outline"
        >
          {t('buttons.loadMore')}
        </Button>
      )}

      <PaymentDetailSheet
        blockUuid={blockUuid}
        onOpenChange={(open) => {
          if (!open) setSelectedPayment(null);
        }}
        open={Boolean(selectedPayment)}
        payment={selectedPayment}
      />
    </div>
  );
};
